/**
 * Staleness check for a configurator session invoice.
 *
 * `POST ${sessionApiUrl}/invoice { session_id }` is IDEMPOTENT per session: once a pending invoice
 * exists it is returned verbatim on every subsequent call — INCLUDING its frozen price + line items
 * + Stripe clientSecret. So a buyer who reaches the Stripe screen, goes Back, switches campaign or
 * edits the feature mix, then returns to checkout is handed the ORIGINAL invoice and would be charged
 * the stale amount. Before loading Stripe we compare the invoice's snapshot to the live session; a
 * mismatch means the pending invoice must be cancelled and a fresh one issued (see
 * `reconcileSessionInvoice` in `./stripe`).
 *
 * This module is deliberately free of store / network imports so the comparison stays a pure,
 * unit-testable function.
 */

/** The parts of a configurator session's `configuration_data` that DETERMINE the invoice amount:
 *  the bound campaign, the selected items (feature → tier), and the applied voucher (which discounts
 *  the first payment). Structurally compatible with the app's `ConfiguratorSessionState`, so the live
 *  session config can be passed straight in. */
export interface SessionInvoiceConfig {
  campaignId?: string;
  items?: Array<{ item_id: number | string; type: string; tier_id: number | string }>;
  /** The applied voucher code (snake_case, as stored in `configuration_data`). Part of the amount
   *  signature because it changes the charged total — an abandoned-cart owner who returns with a new
   *  `?voucher_code=` that supersedes the session's old one must NOT be handed the frozen invoice that
   *  still bills the old discount. The invoice snapshot carries this field (confirmed live on test1). */
  voucher_code?: string | null;
}

/** The invoice row `POST ${sessionApiUrl}/invoice` returns: the full owner-invoice model (same shape
 *  as a `POST ${userApiUrl}/invoice/list` row) PLUS the Stripe `provider_reference` (the PaymentIntent
 *  clientSecret). Only the fields the checkout reconciliation needs are typed. `invoice_data` carries
 *  the frozen SNAPSHOT of the session the invoice was issued for. */
export interface SessionInvoice {
  id: string;
  status?: string;
  /** Whole-currency BASE total as a string (e.g. "14") — NOT the discounted total the wizard shows. */
  price?: string;
  currency?: string;
  provider_reference?: string;
  invoice_data?:
    | ({
        session_data?: {
          configuration_data?: SessionInvoiceConfig;
        } | null;
      } & Record<string, unknown>)
    | null;
}

/** Canonical, order-independent key for an item selection so two selections compare equal iff they
 *  assign the SAME tier to the SAME items (string/number ids normalised). */
function itemsSelectionKey(items: SessionInvoiceConfig['items']): string {
  return (items ?? [])
    .map((it) => `${it.type}:${it.item_id}=${it.tier_id}`)
    .sort()
    .join('|');
}

/**
 * Canonical key for everything that DETERMINES the invoice amount (bound campaign + selected items +
 * applied voucher). Two configs share a signature iff an invoice issued for one is priced correctly
 * for the other, which is exactly the condition for reusing a reconciliation result instead of asking
 * the backend again. Callers memoise on it so re-entering a checkout with an unchanged selection costs
 * zero requests — while a voucher swap (the abandoned-cart override) correctly busts the memo and
 * re-reconciles.
 */
export function sessionConfigSignature(config: SessionInvoiceConfig | undefined | null): string {
  return `${config?.campaignId ?? ''}#${itemsSelectionKey(config?.items)}#${config?.voucher_code ?? ''}`;
}

/**
 * Whether a session invoice still reflects the live session it would charge for. The invoice freezes
 * a snapshot of the session at creation (`invoice_data.session_data.configuration_data`); we treat it
 * as current only when the bound campaign, the selected items, AND the applied voucher — which
 * together determine the price — still match the live session.
 *
 * Comparing the selection rather than the raw `price` string is deliberate: the invoice stores the
 * BASE total while the wizard's "order total" is the DISCOUNTED total, so a number-to-number compare
 * would flag a mismatch on every checkout. Equal campaign + items + voucher ⇒ equal charged amount by
 * construction. The voucher is included so an abandoned-cart owner who returns with a new
 * `?voucher_code=` overriding the session's old one gets the frozen invoice reissued instead of paying
 * the stale discount (the snapshot carries `voucher_code`, confirmed live). A present-or-absent
 * difference in either direction (added / removed / swapped) counts as a change.
 *
 * A missing snapshot can't be verified, so it is reported as a mismatch and the caller reissues.
 */
export function sessionInvoiceMatchesConfig(
  invoice: SessionInvoice,
  config: SessionInvoiceConfig | undefined | null,
): boolean {
  const snapshot = invoice?.invoice_data?.session_data?.configuration_data;
  if (!snapshot) return false;
  const sameCampaign = String(snapshot.campaignId ?? '') === String(config?.campaignId ?? '');
  const sameItems = itemsSelectionKey(snapshot.items) === itemsSelectionKey(config?.items);
  const sameVoucher = String(snapshot.voucher_code ?? '') === String(config?.voucher_code ?? '');
  return sameCampaign && sameItems && sameVoucher;
}

/* ── Payment rails ───────────────────────────────────────────────────────────────────────
 *
 * A session invoice is minted on ONE rail, chosen by the `provider` param of
 * `POST /session/invoice`: absent/`"stripe"` for the card path, `"commerce-gate"` for the SPEI
 * bank transfer the Mexican flows use, and `"gate2way"` for the PIX instant payment. A checkout
 * that offers more than one rail can leave a session holding a pending invoice from the rail the
 * buyer is NOT on any more.
 *
 * The two non-card rails are INSTRUCTION rails: the buyer is shown something to pay against
 * (a CLABE, a PIX code / QR) and pays outside this browser.
 *
 * The row the API returns carries NO `provider` field, so the rail has to be read off the payment
 * artifact the row actually carries. Every test below is deliberately POSITIVE — a row we merely
 * fail to recognise reports `null`, never "another rail" — because the only remedy for a
 * wrong-rail invoice is to CANCEL it, and cancelling voids bank coordinates or a PIX code a buyer
 * may already be paying against.
 */

/** The rails `POST /session/invoice` can mint on. */
export type CheckoutProvider = 'stripe' | 'commerce-gate' | 'gate2way';

/** `provider` value for the card rail. Also the backend default when the param is omitted. */
export const STRIPE_PROVIDER = 'stripe';
/** `provider` value for the SPEI bank-transfer rail. */
export const COMMERCE_GATE_PROVIDER = 'commerce-gate';
/** `provider` value for the PIX instant-payment rail. */
export const GATE2WAY_PROVIDER = 'gate2way';

/** Whether `p` is an instruction rail (SPEI, PIX): the buyer pays outside this browser against
 *  something shown on screen, rather than confirming a card in place. */
export const isInstructionRail = (p: CheckoutProvider): boolean => p !== STRIPE_PROVIDER;

/**
 * Whether a `provider_reference` is a Stripe PaymentIntent client secret (`pi_…_secret_…`).
 *
 * The `_secret_` infix is what makes this positive rather than "the field is non-empty": another
 * provider populating `provider_reference` with its own gateway id must not be mistaken for a
 * chargeable card invoice, since the card rail would then hand that string to `confirmPayment`.
 */
export function isStripeClientSecret(reference: string | undefined | null): boolean {
  return typeof reference === 'string' && reference.includes('_secret_');
}

/** Evidence a caller supplies about an invoice that this module cannot read on its own. */
export interface InvoiceRailEvidence {
  /**
   * The row carries payable SPEI bank coordinates (i.e. a CLABE). Injected rather than derived
   * here so the provider mapping stays with the flow that owns it (`data/spei-details.ts`), and
   * this module stays free of it.
   */
  hasSpeiCoordinates: boolean;
  /**
   * The row carries a PIX "copia e cola" code: positive evidence, a `000201…br.gov.bcb.pix` string
   * in `provider_reference`, computed in `data/pix-details.ts` and injected like
   * `hasSpeiCoordinates`. Optional so callers that only know the SPEI/card rails keep compiling;
   * absent reads as `false`.
   */
  hasPixCode?: boolean;
}

/**
 * The rail an invoice was demonstrably minted on, or `null` when it cannot be told.
 *
 * SPEI coordinates are checked FIRST: they are the strongest evidence, and a row carrying them is
 * a bank transfer whatever else it also carries. A PIX code comes next. The PIX and Stripe checks
 * are mutually exclusive by shape: an EMV code never contains `_secret_`, and a `pi_…_secret_…`
 * never starts with `000201`.
 */
export function readInvoiceProvider(
  invoice: SessionInvoice,
  evidence: InvoiceRailEvidence,
): CheckoutProvider | null {
  if (evidence.hasSpeiCoordinates) return COMMERCE_GATE_PROVIDER;
  if (evidence.hasPixCode === true) return GATE2WAY_PROVIDER;
  if (isStripeClientSecret(invoice?.provider_reference)) return STRIPE_PROVIDER;
  return null;
}

/** Whether the invoice already carries the artifact a checkout on `provider` needs to collect
 *  money: a client secret to confirm (card), bank coordinates to transfer to (SPEI), or a PIX code
 *  to pay (gate2way). */
export function isInvoicePayableOn(
  invoice: SessionInvoice,
  provider: CheckoutProvider,
  evidence: InvoiceRailEvidence,
): boolean {
  switch (provider) {
    case STRIPE_PROVIDER:
      return isStripeClientSecret(invoice?.provider_reference);
    case COMMERCE_GATE_PROVIDER:
      return evidence.hasSpeiCoordinates;
    case GATE2WAY_PROVIDER:
      return evidence.hasPixCode === true;
  }
}

/** The money already arrived (a settled invoice, or one a voucher covered in full on creation).
 *  Terminal: never stale, never reissued, never cancelled. */
export const isInvoicePaid = (invoice: SessionInvoice | null | undefined): boolean =>
  invoice?.status === 'paid';

/** Still open, so `POST /session/cancel-invoice` can void it. Anything else is already closed and
 *  cancelling it just returns `INVOICE_NOT_PENDING`. */
export const isInvoicePending = (invoice: SessionInvoice | null | undefined): boolean =>
  invoice?.status === 'pending';

/**
 * The invoice can still take part in a checkout: it is awaiting payment, or already settled.
 *
 * Anything else — `cancelled` above all — is finished, and must never be handed to a buyer. This
 * matters because `GET /session/invoice` is STALE: probed live on test1 (2026-07-31), after
 * switching rails it kept returning the session's FIRST invoice, cancelled, while
 * `session.invoice_id` correctly pointed at the newest one. So the read our reconcile starts from
 * routinely returns a closed invoice.
 *
 * Today a cancelled row also comes back stripped of its `provider_reference` and its
 * `payment_instructions`, so the payability checks reject it anyway — but only by accident. A
 * closed invoice that kept its artifacts would otherwise be judged usable, which means handing
 * Stripe a cancelled PaymentIntent, or showing a buyer a CLABE that no longer belongs to their
 * order. Checking the status makes the guarantee explicit instead of incidental.
 */
export const isInvoiceOpen = (invoice: SessionInvoice | null | undefined): boolean =>
  isInvoicePending(invoice) || isInvoicePaid(invoice);

/**
 * Whether a `GET /session/invoice` result can be POSITIVELY attributed to the session's current
 * invoice — the only case in which its status may be believed.
 *
 * It frequently cannot. That read returns the session's FIRST invoice rather than its live one
 * (probed on test1, 2026-07-31), while the session envelope's `invoice_id` tracks the real one. So
 * any session whose invoice was ever superseded — the buyer switched rails, or changed their pack
 * and the stale invoice was reissued — gets back a `cancelled` row describing an invoice nobody
 * cares about any more.
 *
 * Reporting that row's status as THE status is how a buyer who had just paid by card was told at
 * the deploy step that their payment was never made: `isDeployBlockedUnpaid('cancelled')` is
 * `true`, and the gate's deliberate fail-open for unknown statuses never got a chance to help,
 * because a confident wrong answer is not an unknown one.
 *
 * **An unknown session pointer is UNATTRIBUTABLE, not a licence to believe the read.** An earlier
 * version accepted the read whenever the session's own id was missing, reasoning that there was
 * nothing to contradict. That reads the wrong way round on the path that matters: the deploy step
 * fires this read on mount, off a `sessionId` rehydrated synchronously from localStorage, so it
 * routinely resolves while `/session/load` is still in flight and the pointer is still null. The
 * superseded row was then judged "current" and applied — the original bug, reproduced by a page
 * reload. Both ids must be present and equal; anything else is UNKNOWN, which is the case
 * `isDeployBlockedUnpaid` is designed to fail open on.
 */
export function isAttributableInvoiceRead(
  readInvoiceId: string | null | undefined,
  sessionInvoiceId: string | null | undefined,
): boolean {
  if (!readInvoiceId || !sessionInvoiceId) return false;
  return readInvoiceId === sessionInvoiceId;
}

/** Why an invoice cannot be used for a checkout on a given rail. */
export type InvoiceRejection =
  /** It is finished (cancelled, expired, …), so it can take no further part in a checkout. */
  | 'not-open'
  /** Its frozen snapshot no longer matches the live selection, so it would bill the old amount. */
  | 'stale'
  /** It is demonstrably ANOTHER rail's invoice. */
  | 'wrong-provider'
  /** It is this rail's invoice (or unreadable) but carries nothing to collect money with. */
  | 'not-payable';

/**
 * Why a returned invoice can't be used for a checkout on `provider`, or `null` when it can.
 *
 * Four independent reasons, in the order they matter:
 *
 * 0. **`not-open`** — it is closed (cancelled/expired). Checked FIRST because it disqualifies the
 *    invoice outright: no comparison of rail or selection can rescue a finished invoice, and
 *    `GET /session/invoice` hands us closed ones routinely (see {@link isInvoiceOpen}).
 * 1. **`stale`** — the invoice froze a snapshot of the session at creation, and the buyer has since
 *    gone Back and changed their pack or features. Charging the frozen amount is wrong on the card
 *    rail and worse on SPEI, where a wrong figure is a wrong bank transfer rather than a declined
 *    card. Only an actually-present, actually-different snapshot counts: a rail whose envelope
 *    simply omits the snapshot must not look stale on every single call.
 * 2. **`wrong-provider`** — positive evidence of another rail. The backend checks idempotency
 *    BEFORE it dispatches to a provider, so a pending invoice from the rail the buyer just switched
 *    away from is handed straight back to the new rail's call, artifact and all (verified live).
 *    Without this branch the checkout dead-ends permanently: every retry re-fetches the same
 *    unpayable invoice.
 * 3. **`not-payable`** — reported only for the card rail, and deliberately so. A card checkout
 *    cannot even start without a client secret, so a row that has none has to be replaced. On BOTH
 *    instruction rails (SPEI, PIX) the same absence is left UNREPORTED: cancelling is destructive,
 *    so a payload we merely failed to parse must not trigger it — it falls through to the caller's
 *    own "no CLABE" / "no code" guard, which surfaces a retry without destroying anything.
 */
export function invoiceRejectionFor(
  invoice: SessionInvoice,
  config: SessionInvoiceConfig | undefined | null,
  provider: CheckoutProvider,
  evidence: InvoiceRailEvidence,
): InvoiceRejection | null {
  if (!isInvoiceOpen(invoice)) return 'not-open';

  const hasSnapshot = Boolean(invoice?.invoice_data?.session_data?.configuration_data);
  if (hasSnapshot && !sessionInvoiceMatchesConfig(invoice, config)) return 'stale';

  const actual = readInvoiceProvider(invoice, evidence);
  if (actual != null && actual !== provider) return 'wrong-provider';

  if (provider === STRIPE_PROVIDER && !isInvoicePayableOn(invoice, provider, evidence)) {
    return 'not-payable';
  }
  return null;
}

/**
 * True when `rows` (a `POST /invoice/list` response's `data`) contains a `paid` invoice for
 * `sessionId`. The client-side re-check of `session_id` + `status` is the MONEY-SAFETY BELT: even if
 * the backend ever ignored the `{session_id, status:'paid'}` filter, a covering-paid match can never
 * be claimed off another session's row or a non-paid one. Pure + exported so the belt is unit-tested.
 */
export function hasPaidInvoiceForSession(rows: unknown, sessionId: string): boolean {
  return (
    Array.isArray(rows) &&
    rows.some(
      (r) =>
        !!r &&
        typeof r === 'object' &&
        (r as { session_id?: unknown }).session_id === sessionId &&
        (r as { status?: unknown }).status === 'paid',
    )
  );
}
